// Minimal ELK layered layout for the provenance DAG.
//
// Follows the same conventions as src/components/dag/dagLayout.ts (ORTHOGONAL
// routing, DOWN direction, layered algorithm) but duplicated here so we don't
// reach into Thread's core DAG module. Each node kind has its own fixed size.

import ELK, { type ElkNode } from 'elkjs/lib/elk.bundled.js';
import type { Node, Edge } from '@xyflow/react';

const elk = new ELK();

export const NODE_SIZES = {
  campaign: { width: 280, height: 120 },
  batch:    { width: 160, height: 70 },
  point:    { width: 72,  height: 72 },
  surrogate:{ width: 220, height: 80 },
  fork:     { width: 220, height: 88 },
} as const;

export type ProvenanceNodeType = keyof typeof NODE_SIZES;

export async function layoutProvenance(
  nodes: Node[],
  edges: Edge[],
): Promise<{ nodes: Node[]; edges: Edge[] }> {
  const graph: ElkNode = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'DOWN',
      'elk.spacing.nodeNode': '28',
      'elk.layered.spacing.nodeNodeBetweenLayers': '70',
      'elk.edgeRouting': 'ORTHOGONAL',
      'elk.spacing.edgeNode': '20',
    },
    children: nodes.map((n) => {
      const size = NODE_SIZES[(n.type as ProvenanceNodeType) ?? 'point'] ?? NODE_SIZES.point;
      return { id: n.id, width: size.width, height: size.height };
    }),
    edges: edges.map((e) => ({ id: e.id, sources: [e.source], targets: [e.target] })),
  };

  let result;
  try {
    result = await Promise.race([
      elk.layout(graph),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('ELK layout timed out')), 4000),
      ),
    ]);
  } catch (err) {
    // Fallback: column-grid so the user still sees something.
    // eslint-disable-next-line no-console
    console.warn('Provenance ELK layout failed; using fallback grid', err);
    return {
      nodes: nodes.map((n, i) => ({
        ...n,
        position: { x: (i % 8) * 180, y: Math.floor(i / 8) * 160 },
      })),
      edges,
    };
  }

  const posMap = new Map((result.children ?? []).map((c) => [c.id, c]));
  const positionedNodes = nodes.map((n) => {
    const p = posMap.get(n.id);
    return { ...n, position: { x: p?.x ?? 0, y: p?.y ?? 0 } };
  });

  return { nodes: positionedNodes, edges };
}
